import ExtensionIcon from '@/assets/icons/ExtensionIcon';
import TeamRoleIcon from '@/assets/icons/TeamRoleIcon';
import { TeamMember, getTeamMemberDisplayName } from 'apis/business/setting/team/types';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Collapse
} from '@mui/material';
import { useState } from 'react';
import {
  MdAccessTime,
  MdExpandLess,
  MdExpandMore,
  MdMailOutline,
  MdMoreVert,
  MdOutlineEdit,
  MdOutlineLocalPhone
} from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';

const ROLE_LABELS: Record<string, string> = {
  business_admin: 'Business Admin',
  location_admin: 'Location Admin',
  operator: 'Operator'
};

export default function TeamMemberCard({
  member,
  onEdit,
  onDelete,
  idx
}: {
  member: TeamMember;
  onEdit: () => void;
  onDelete: () => void;
  idx: number;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(idx === 0);
  const theme = useTheme();

  const handleCloseMenu = () => setAnchorEl(null);

  const renderBreaks = (breaks?: { from: string; to: string }[]) => {
    if (!breaks || breaks.length === 0) {
      return (
        <Typography color='text.disabled' textAlign='center'>
          —
        </Typography>
      );
    }

    return (
      <Box display='flex' flexDirection='column' alignItems='center'>
        {breaks.map((b, index) => (
          <Typography key={index} fontSize={14} color={theme.palette.primary.main}>
            {b.from} - {b.to}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderBusinessHour = (day: string) => {
    const data = member.working_hours?.[day.toLowerCase()];
    if (!data) return '—';
    return data.open && data.from_time && data.to_time ? `${data.from_time} - ${data.to_time}` : 'Closed';
  };

  const roleLabel = member.role ? ROLE_LABELS[member.role] || member.role.replace(/_/g, ' ') : null;

  return (
    <Box border='1px solid #ECECEC' borderRadius='12px' p={3} bgcolor='#fff'>
      <Box display='flex' justifyContent='space-between'>
        <Box>
          <Typography fontWeight={500}>{getTeamMemberDisplayName(member)}</Typography>

          <Stack direction='row' spacing={4} mt={0.5} flexWrap='wrap' useFlexGap>
            {member.email && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <MdMailOutline color={theme.palette.primary.main} />
                <Typography variant='body2'>{member.email}</Typography>
              </Stack>
            )}
            {member.phone_number && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <MdOutlineLocalPhone color={theme.palette.primary.main} />
                <Typography variant='body2'>{member.phone_number}</Typography>
              </Stack>
            )}
            {member.sip_uri && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <ExtensionIcon color={theme.palette.primary.main} />
                <Typography variant='body2'>{member.sip_uri}</Typography>
              </Stack>
            )}
            {member.extension && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <ExtensionIcon color={theme.palette.primary.main} />
                <Typography variant='body2'>{member.extension}</Typography>
              </Stack>
            )}
            {roleLabel && (
              <Stack direction='row' spacing={1} alignItems='center'>
                <TeamRoleIcon />
                <Typography variant='body2'>{roleLabel}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MdMoreVert />
        </IconButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onEdit();
            }}
          >
            <MdOutlineEdit /> Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onDelete();
            }}
            sx={{ color: 'red' }}
          >
            <RiDeleteBin6Line /> Delete
          </MenuItem>
        </Menu>
      </Box>

      <Box mt={2} border='1px solid #E5E7EB' borderRadius={2} p={1.5} bgcolor='#FFFFFF' sx={{ overflow: 'hidden' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpen(!open)}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <MdAccessTime color={theme.palette.primary.main} size={18} />
            <Typography variant='body2' fontWeight={500}>
              Office Hours
            </Typography>
          </Stack>
          {open ? <MdExpandLess /> : <MdExpandMore />}
        </Stack>

        <Collapse in={open}>
          <Box
            mt={1.5}
            border='1px solid #E5E7EB'
            borderRadius={2}
            overflow='auto'
            sx={{
              backgroundColor: '#FFFFFF',
              width: '100%',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: 4
              }
            }}
          >
            <Table
              size='small'
              sx={{
                minWidth: 700,
                borderCollapse: 'separate',
                borderSpacing: 0
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid #E5E7EB' }}> {} </TableCell>
                  {days.map((day) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        borderRight: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {day}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      borderRight: '1px solid #E5E7EB',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    SCHEDULE
                  </TableCell>
                  {days.map((day) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        color: theme.palette.primary.main,
                        borderRight: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderBusinessHour(day)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      borderRight: '1px solid #E5E7EB'
                    }}
                  >
                    BREAKS
                  </TableCell>
                  {days.map((day) => (
                    <TableCell
                      key={day}
                      align='center'
                      sx={{
                        color: theme.palette.primary.main,
                        borderRight: '1px solid #E5E7EB',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {renderBreaks(member?.working_hours?.[day.toLowerCase()]?.breaks)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
